import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Generates a simple device hash from browser properties.
 * Not a full fingerprint library, but catches most multi-account scenarios.
 */
const generateDeviceHash = (): string => {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth?.toString() || '',
    new Date().getTimezoneOffset().toString(),
    navigator.hardwareConcurrency?.toString() || '',
  ];
  // Simple hash
  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return 'dh_' + Math.abs(hash).toString(36);
};

/**
 * Logs device fingerprint data on login. Fire-and-forget, non-blocking.
 */
export const useDeviceFingerprint = () => {
  const hasLogged = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user && !hasLogged.current) {
        hasLogged.current = true;

        const deviceHash = generateDeviceHash();
        const screenInfo = {
          width: screen.width,
          height: screen.height,
          colorDepth: screen.colorDepth,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        // Fire and forget — don't block UI
        supabase.functions.invoke('log-device-fingerprint', {
          body: {
            event_type: 'login',
            device_hash: deviceHash,
            screen_info: screenInfo,
          },
        }).catch(() => {
          // Silent fail — fingerprinting should never affect UX
        });
      }

      if (event === 'SIGNED_OUT') {
        hasLogged.current = false;
      }
    });

    return () => subscription.unsubscribe();
  }, []);
};
